// Core
import { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  Flex,
  Grid,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { formatDateWithSuffix } from 'services/Utils';
import placeholderPlan from 'assets/img/empty-plan.png';

const CANONICAL_YEARLY_TYPES = ['entry', 'premium', 'elite'];
const formatMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const membershipSuffix = (name) => (/membership\s*$/i.test(name || '') ? '' : ' membership');
const normalizeError = (message) =>
  Array.isArray(message) ? message.join(', ') : message || 'Something went wrong. Please try again.';

const YearlyUpgradeModal = ({ isOpen, onClose, userData }) => {
  const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
  const cardBg = useColorModeValue('white', '#0a1027');
  const subColor = useColorModeValue('#7787B0', '#8794C2');

  const subscriptionId = userData?.activeSubscription?.subscriptionId;

  const [step, setStep] = useState('list'); // 'list' | 'confirm' | 'success'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: plansData, isLoading: isPlansLoading } = useQuery({
    queryKey: ['yearly-subscription-plans'],
    queryFn: async () => {
      const response = await apiService.getPlansByType({ type: 'subscriptions' });
      return response.data;
    },
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const currentPriceId = userData?.activePlan?.priceId;
  const yearlyByTier = (plansData ?? [])
    .filter(
      (plan) =>
        plan.paymentType === 'yearly' &&
        plan.isUpgrade === true &&
        CANONICAL_YEARLY_TYPES.includes(plan.type) &&
        plan.priceId !== currentPriceId,
    )
    .reduce((acc, plan) => {
      const existing = acc[plan.type];
      if (!existing || (plan.id ?? 0) > (existing.id ?? 0)) {
        acc[plan.type] = plan;
      }
      return acc;
    }, {});
  const yearlyPlans = CANONICAL_YEARLY_TYPES.map((type) => yearlyByTier[type]).filter(Boolean);

  const resetAndClose = () => {
    setStep('list');
    setSelectedPlan(null);
    setPreview(null);
    setResult(null);
    setError('');
    setIsActionLoading(false);
    onClose();
  };

  const handleSelectPlan = async (plan) => {
    if (isActionLoading) {
      return;
    }
    setError('');
    setSelectedPlan(plan);
    setIsActionLoading(true);

    try {
      const response = await apiService.previewYearlyUpgrade({
        subscriptionId,
        price: plan.priceId,
      });

      if (response?.message === 'Success' && response?.data) {
        setPreview(response.data);
        setStep('confirm');
      } else {
        setError(normalizeError(response?.message));
      }
    } catch (e) {
      setError('Could not load the upgrade preview. Please try again.');
    }

    setIsActionLoading(false);
  };

  const handleConfirm = async () => {
    if (isActionLoading) {
      return;
    }
    setError('');
    setIsActionLoading(true);

    try {
      const response = await apiService.upgradeYearly({
        subscriptionId,
        price: selectedPlan.priceId,
      });

      if (response?.message === 'Success' && response?.data) {
        setResult(response.data);
        setStep('success');
      } else {
        setError(normalizeError(response?.message));
      }
    } catch (e) {
      setError('Upgrade failed. Please check your payment method and try again.');
    }

    setIsActionLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={resetAndClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        className="modal__simple"
        maxW={{ sm: step === 'list' ? '720px' : '560px' }}
        style={{ border: '1px solid #2ABDFD' }}>
        <ModalCloseButton style={{ zIndex: 9 }} />

        {/* STEP 1 — list yearly plans */}
        {step === 'list' && (
          <>
            <ModalHeader pt="24px" textAlign="center" fontSize="28px" fontWeight="700" fontStyle="italic">
              Upgrade to a Yearly Plan
            </ModalHeader>
            <ModalBody pb="24px">
              <Text textAlign="center" color={subColor} mb="20px">
                Choose a yearly membership. Your unused Everyday Saver time will be applied as a credit.
              </Text>

              {error && <Text color="#FF3B30" textAlign="center" mb="16px">{error}</Text>}

              {isPlansLoading ? (
                <Flex justify="center" py="40px"><Spinner /></Flex>
              ) : yearlyPlans.length === 0 ? (
                <Text textAlign="center" color={subColor} py="32px">
                  No yearly plans are available right now. Please try again later.
                </Text>
              ) : (
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap="12px">
                  {yearlyPlans.map((plan) => (
                    <Box
                      key={plan.priceId}
                      as="button"
                      textAlign="left"
                      onClick={() => handleSelectPlan(plan)}
                      disabled={isActionLoading}
                      p="16px"
                      borderRadius="16px"
                      border={border}
                      bg={cardBg}
                      _hover={{ borderColor: '#2ABDFD' }}
                      opacity={isActionLoading && selectedPlan?.priceId !== plan.priceId ? 0.6 : 1}
                      position="relative">
                      <Image
                        src={plan.image || placeholderPlan}
                        alt={plan.name}
                        w="40px"
                        h="40px"
                        mb="8px" />
                      <Text variant="title-lg" fontSize="18px" fontWeight="800">{plan.name}</Text>
                      <Text mt="4px" fontSize="22px" fontWeight="700">
                        {formatMoney(plan.price)}
                        <Text as="span" fontSize="14px" color={subColor}> /year</Text>
                      </Text>
                      <Text mt="8px" fontSize="14px" color={subColor}>
                        {plan.entries} entries included
                      </Text>
                      {plan.description && (
                        <Text mt="8px" fontSize="13px" color={subColor}>{plan.description}</Text>
                      )}
                      {isActionLoading && selectedPlan?.priceId === plan.priceId && (
                        <Flex position="absolute" inset="0" align="center" justify="center"
                          bg="rgba(0,0,0,0.25)" borderRadius="16px">
                          <Spinner color="white" />
                        </Flex>
                      )}
                    </Box>
                  ))}
                </Grid>
              )}
            </ModalBody>
          </>
        )}

        {step === 'confirm' && preview && (
          <>
            <ModalHeader pt="24px" textAlign="center" fontSize="28px" fontWeight="700" fontStyle="italic">
              Confirm Upgrade
            </ModalHeader>
            <ModalBody pb="24px">
              <Box textAlign="center" px={{ base: '0', md: '20px' }}>
                <Text>
                  You are upgrading from <b>{preview.currentPlanName}</b> to <b>{preview.newPlanName}</b>.
                </Text>
                <Text mt="12px">
                  Your unused {preview.currentPlanName} time is worth{' '}
                  <b>{formatMoney(preview.creditAmount)}</b> and will be applied as a credit.
                </Text>
                <Text mt="12px">
                  You will be charged <b>{formatMoney(preview.chargeAmount)}</b> today.
                </Text>
                <Text mt="12px">
                  Your new yearly membership will renew on{' '}
                  <b>{formatDateWithSuffix(preview.renewalDate)}</b>.
                </Text>

                {error && <Text color="#FF3B30" mt="16px">{error}</Text>}

                <Flex mt="32px" gap="12px">
                  <Button
                    variant="secondary-md"
                    onClick={() => { setStep('list'); setError(''); }}
                    isDisabled={isActionLoading}>
                    Deny Upgrade
                  </Button>
                  <Button variant="button-md" onClick={handleConfirm} isLoading={isActionLoading}>
                    Confirm Upgrade
                  </Button>
                </Flex>
              </Box>
            </ModalBody>
          </>
        )}

        {step === 'success' && result && (
          <>
            <ModalHeader pt="24px" textAlign="center" fontSize="28px" fontWeight="700" fontStyle="italic">
              Upgrade Successful
            </ModalHeader>
            <ModalBody pb="24px">
              <Box textAlign="center" px={{ base: '0', md: '20px' }}>
                <Text>
                  Your <b>{result.newPlanName}</b>{membershipSuffix(result.newPlanName)} is now active and{' '}
                  <b>{result.entries} entries</b> have been added to your account.
                </Text>
                <Text mt="12px">
                  Your membership will renew on <b>{formatDateWithSuffix(result.renewalDate)}</b>.
                </Text>

                <Flex mt="32px" justify="center">
                  <Button
                    variant="button-md"
                    onClick={() => { window.location.href = '/account/membership'; }}>
                    Go to Membership
                  </Button>
                </Flex>
              </Box>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default YearlyUpgradeModal;
